'use no memo';

import { useNavigate } from '@tanstack/react-router';
import type { Row, SortingState } from '@tanstack/react-table';
import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { DataTable, DataTablePagination, useTableDisplayState } from '@/components/DataTable';
import { ListToolbar } from '@/components/Layout';
import SearchInput from '@/components/SearchInput';
import { ToggleHelpText } from '@/components/ToggleHelpText';
import { Text } from '@/components/Typography/Text';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { ContractTypeFilterChip } from './-ContractTypeFilterChip';
import { type ContractAlertItem, type ListContractAlertsQuery, useContractAlerts } from './-query';
import { useAlertColumns } from './-useAlertColumns';

/**
 * 「契約終了予定」カテゴリの中身。
 * BE の contract-alert API（GET /v2/contract-ledger/alert）を使用。
 */
export function ExpiryDueAlert() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.contract.list'
  });
  const navigate = useNavigate();
  const columns = useAlertColumns();
  const [selectedTypes, setSelectedTypes] = useState<NonNullable<ListContractAlertsQuery['contractTypes']>>([]);
  const [freeText, setFreeText] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100
  });
  const resetPage = () => setPagination(prev => ({
    ...prev,
    pageIndex: 0
  }));
  const handleFilterInput = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFreeText(event.target.value);
    resetPage();
  }, [], {
    delay: 300
  });
  const handleTypeChange = (next: string[]) => {
    setSelectedTypes(next as NonNullable<ListContractAlertsQuery['contractTypes']>);
    resetPage();
  };
  const orderBy = useMemo((): ListContractAlertsQuery['orderBy'] => {
    const col = sorting[0];
    if (!col) return undefined;
    const map: Record<string, ListContractAlertsQuery['orderBy']> = {
      contractNo: 'CONTRACT_NO',
      contractTitle: 'CONTRACT_TITLE',
      contractStatus: 'CONTRACT_STATUS',
      contractStartDate: 'CONTRACT_START_DATE',
      contractEndDate: 'CONTRACT_END_DATE'
    };
    return map[col.id];
  }, [sorting]);
  const orderDirection = useMemo((): ListContractAlertsQuery['orderDirection'] => {
    const col = sorting[0];
    if (!col) return 'asc';
    return col.desc ? 'desc' : 'asc';
  }, [sorting]);
  const queryParams = useMemo((): ListContractAlertsQuery => ({
    category: 'EXPIRY_DUE',
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    contractTypes: selectedTypes.length > 0 ? selectedTypes : undefined,
    freeText: freeText.trim() || undefined,
    orderBy,
    orderDirection
  }), [pagination, selectedTypes, freeText, orderBy, orderDirection]);
  const {
    data,
    isPending,
    isPlaceholderData
  } = useContractAlerts(queryParams);
  const items = data?.items ?? [];
  const totalCount = data?.count ?? 0;
  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: data?.maxPage ?? 0,
    initialState: {
      columnPinning: {
        left: ['contractNo'],
        right: ['actions']
      }
    },
    state: {
      sorting,
      pagination
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    meta: {
      getRowClassName: () => 'h-14'
    }
  });
  const displayState = useTableDisplayState(isPending, items);
  const handleRowClick = (row: Row<ContractAlertItem>) => {
    const contractId = row.original.contractId;
    if (!contractId) return;
    navigate({
      to: '/contract/$id',
      params: {
        id: contractId
      }
    });
  };
  return <>
      <ToggleHelpText title={t('alerts.expiryDue.helpTitle')} className="p-0 text-left">
        <Text variant="pSmall">{t('alerts.expiryDue.helpBody')}</Text>
      </ToggleHelpText>

      <ListToolbar className="p-2 rounded bg-background">
        <SearchInput className="w-56 h-8 shrink-0" placeholder={t('filterPlaceholder')} defaultValue={freeText} onChange={handleFilterInput} />
        <ContractTypeFilterChip value={selectedTypes} onChange={handleTypeChange} />
      </ListToolbar>

      <div className={cn('flex flex-col flex-1 gap-4 min-h-0',
    // 再取得中（keepPreviousData で前ページ表示中）はテーブルとページネーションを
    // まとめて薄く表示し、フィルタ・ソート変更中に古い件数・総ページ数が
    // 現条件の値のように見えるのを防ぐ。
    isPlaceholderData && 'opacity-50 transition-opacity')}>
        <div className="overflow-auto flex-1 min-h-0">
          <DataTable table={table} displayState={displayState} onRowClick={handleRowClick} isHoverable />
        </div>

        <DataTablePagination table={table} totalCount={totalCount} />
      </div>
    </>;
}